import React, { useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import {
  LayoutDashboard, Store, ScrollText, Settings, CreditCard,
  Globe, ChevronDown, Menu, X, Download,
} from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { SUPPORTED_LOCALES, LANG_LABELS, type Locale } from '@/infrastructure/i18n';
import { LogoBadge, LogoText } from './Logo';
import { AccountSwitcherDesktop, AccountSwitcherMobile, AccountAvatarMobile } from './AccountSwitcher';
import { NotificationBell } from '../NotificationBell';
import { useProfile } from '@/hooks/useCommonQueries';
import { useFiscalSetupGate } from '@/hooks/useFiscalSetupGate';
import { API_BASE } from '@/infrastructure/api/client';

const navItems = [
  { key: 'nav.dashboard', href: '/', icon: LayoutDashboard },
  { key: 'nav.stores', href: '/stores', icon: Store, match: '/stores' },
  { key: 'nav.audit', href: '/audit', icon: ScrollText },
  { key: 'nav.billing', href: '/billing', icon: CreditCard },
  { key: 'nav.settings', href: '/settings', icon: Settings },
] as const;

export const ConsoleLayout: React.FC = () => {
  const { t, locale, setLocale } = useI18n();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { data: profileData } = useProfile();
  const isTrial = profileData?.profile.status === 'trial';
  const trialDays = isTrial && profileData?.profile.trial_ends_at
    ? Math.max(0, Math.ceil((profileData.profile.trial_ends_at - Date.now()) / 86_400_000))
    : null;

  // Production fiscal identity gate (spec §9.2 / INDEX C3): paid-lane tenants
  // with an unprovisioned production identity are routed to /fiscal-setup before
  // they can reach the console. Single source of truth in useFiscalSetupGate;
  // StoreLayout applies the same gate so the /stores/:id/* routes can't bypass.
  const { needsFiscalSetup } = useFiscalSetupGate();
  if (needsFiscalSetup && location.pathname !== '/fiscal-setup') {
    return <Navigate to="/fiscal-setup" replace />;
  }

  const isActive = (href: string, match?: string) => {
    if (match) return location.pathname.startsWith(match);
    return location.pathname === href;
  };

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 flex-col bg-white border-r border-slate-200">
        <div className="h-16 flex items-center px-5 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2">
            <LogoBadge />
            <LogoText />
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 ${
                isActive(item.href, 'match' in item ? item.match : undefined)
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <item.icon className="w-4.5 h-4.5" />
              <span>{t(item.key)}</span>
            </Link>
          ))}
        </nav>

        {isTrial && trialDays !== null && (
          <div className="mx-3 mb-1 px-3 py-2 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 font-medium">
              {t('trial.sidebar_badge', { days: trialDays })}
            </p>
          </div>
        )}

        <div className="px-3 pb-4 space-y-2">
          {/* Language */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-50 cursor-pointer"
            >
              <Globe className="w-4 h-4" />
              <span>{LANG_LABELS[locale]}</span>
              <ChevronDown className="w-3 h-3 ml-auto" />
            </button>
            {langOpen && (
              <div className="absolute bottom-full left-0 mb-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10">
                {SUPPORTED_LOCALES.map(lang => (
                  <button
                    key={lang}
                    onClick={() => { setLocale(lang as Locale); setLangOpen(false); }}
                    className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer ${locale === lang ? 'text-primary-500 font-medium' : 'text-slate-600'}`}
                  >
                    {LANG_LABELS[lang]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
            <AccountSwitcherDesktop />
            <NotificationBell />
          </div>
          <div className="flex items-center gap-1.5 px-3 pb-1">
            <a href={`${API_BASE}/api/download/latest`} title="Download POS" className="text-slate-300 hover:text-primary-500 transition-colors">
              <Download className="w-3 h-3" />
            </a>
          </div>
        </div>
      </aside>

      {/* Mobile + Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Mobile header */}
        <header className="md:hidden h-14 pt-safe flex items-center justify-between px-4 bg-white border-b border-slate-200 shrink-0 relative z-50">
          <Link to="/" className="flex items-center gap-2">
            <LogoBadge size="sm" />
            <LogoText className="text-base" />
          </Link>
          <div className="flex items-center gap-2">
            <AccountAvatarMobile />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="text-slate-600 cursor-pointer">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Mobile nav overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity" 
            onClick={() => setMobileOpen(false)} 
          />
          
          {/* Drawer */}
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out h-full">
            <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <LogoBadge />
                <LogoText />
              </Link>
              <button 
                onClick={() => setMobileOpen(false)}
                className="p-2 -mr-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    isActive(item.href, 'match' in item ? item.match : undefined)
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{t(item.key)}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{t('settings.language')}</p>
                <div className="flex flex-wrap gap-2">
                  {SUPPORTED_LOCALES.map(lang => (
                    <button
                      key={lang}
                      onClick={() => setLocale(lang as Locale)}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        locale === lang
                          ? 'bg-white border-primary-200 text-primary-600 font-medium shadow-sm'
                          : 'bg-transparent border-transparent text-slate-500 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      {LANG_LABELS[lang]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{t('account.title')}</p>
                <AccountSwitcherMobile onClose={() => setMobileOpen(false)} />
              </div>

              <div className="flex items-center justify-center gap-1.5 px-4">
                <a href={`${API_BASE}/api/download/latest`} title="Download POS" className="text-slate-400 hover:text-primary-500 transition-colors">
                  <Download className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
